import React, { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { localized } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { SkioldButton } from 'web/view/components/skiold-components/skiold-button/skiold-button';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { SaveMoveEvent } from 'shared/state/ducks/move-events/operations';
import { Equipment, Gender, IMoveEvent } from 'shared/api/api';
import { ValidateMoveEvent } from 'shared/helpers/stemanimal-helper/stemanimal-helper';
import { showAlert, ShowConfirmAlert } from 'web/view/components/skiold-alert/skiold-alert';
import { useYoungAnimalMoveEvent } from 'shared/helpers/move-young-animal-helper/move-young-animal-provider';

export interface Props {
	closeEditModal?: () => void;
}

export const MoveYoungAnimalButtons: FC<Props> = memo(props => {
	const { state, setFullState } = useYoungAnimalMoveEvent();

	const esfExists = useSelector(
		(state: WebAppState) =>
			state.processEquipments.entities.find(pq => pq.equipment === Equipment.ESF) !== undefined,
	);

	const pens = useSelector((state: WebAppState) => state.locations.pens);
	const unitToPens = useSelector((state: WebAppState) => state.unitToPenData);
	const processEquipmentData = useSelector((state: WebAppState) => state.processEquipmentData);

	const dispatch = useDispatch();

	const moveEvent = useMemo(() => {
		if (!state.date) {
			return;
		}
		const event: IMoveEvent = {
			moveDate: state.date!,
			isDeleted: false,
			penId: state.penId,
			stemAnimalId: state.youngAnimal?.id,

			siteId: state.youngAnimal?.siteId,
			feedCurveNumber: state.feedCurveNumber,
			fixedDeviation: state.fixedDeviation,
			deviationPeriod: state.deviationPeriod,
			tempFeedDeviation: state.tempFeedDeviation,
			transponder: state.transponderNr?.toString(),
		};
		return event;
	}, [state]);

	const save = useCallback(async () => {
		if (!moveEvent) {
			return;
		}

		const isValid = await ValidateMoveEvent(
			state.youngAnimal,
			moveEvent,
			state.sectionId,
			esfExists,
			pens,
			[],
			unitToPens.data,
			processEquipmentData.entities,
			undefined,
			showAlert,
			ShowConfirmAlert,
		);

		if (!isValid) {
			return;
		}

		dispatch(SaveMoveEvent(moveEvent, 0));

		// Reset form after submit
		setFullState({
			date: new Date(),
			gender: Gender.Female,
			penId: state.penId,
			sectionId: state.sectionId,
			feedCurveNumber: state.feedCurveNumber,
			fixedDeviation: state.fixedDeviation,
			deviationPeriod: state.deviationPeriod,
			tempFeedDeviation: state.tempFeedDeviation,
		});
	}, [
		dispatch,
		esfExists,
		moveEvent,
		pens,
		processEquipmentData.entities,
		setFullState,
		state.deviationPeriod,
		state.feedCurveNumber,
		state.fixedDeviation,
		state.penId,
		state.sectionId,
		state.tempFeedDeviation,
		state.youngAnimal,
		unitToPens.data,
	]);

	return (
		<ViewWeb className="button-view-style-upper-corner">
			{props.closeEditModal && (
				<SkioldButton title={localized('Close')} onPress={props.closeEditModal} theme="grey" />
			)}
			<SkioldButton title={localized('Save')} onPress={save} />
		</ViewWeb>
	);
});
